import { useEffect, useId, useMemo, useRef, useState, type KeyboardEvent } from "react";
import { Link, useLocation } from "react-router-dom";
import { ChevronDown, GitBranch, Search } from "lucide-react";
import { isAdoName, isGitBranchName, type GroomingRepoInput } from "@ttc-spectree/toolbox-contract";
import { Button } from "@/components/ui/button";
import { useAdoBranches, useAdoRepositories } from "@/hooks/queries/use-grooming";
import { useWorkItemProjects } from "@/hooks/queries/use-work-items";
import type { AdoBranch, AdoRepository } from "@/lib/api/grooming";
import { getAdoPatErrorCode, getApiErrorCode } from "@/lib/api/work-items";
import { adoSetupRedirect } from "@/lib/toolbox/page-names";
import { TOOLBOX_TEXT } from "@/lib/toolbox/type-scale";
import { cn } from "@/lib/utils";

/**
 * The Admin's new-repository row on the Repos page (epic PI-SPEC-SW-E288,
 * feature PI-SPEC-SW-1467, Claude Design "Getting Started Mockups" frame 1e;
 * 03-evaluation.md section 8.6 Q7: "no user types ADO project names, branches
 * and the like").
 *
 * - Repository: a searchable combobox fed by `GET …/ado-repositories`, under
 *   the eyebrow "Repositories in <project>". Each entry shows its default
 *   branch; one the project already maps is greyed with "already added" and
 *   cannot be picked, and an empty repository (no default branch) neither.
 *   "Already added" is the API's `alreadyAdded`, which since epic
 *   PI-SPEC-SW-E318 also counts a GitHub repository with the same name in the
 *   project (names are unique per ADO project across hosts); the picker adds
 *   no rule of its own.
 * - Branch: a list fed by `GET …/ado-repositories/:id/branches`, defaulting to
 *   the repository's default branch. Disabled until a repository is picked.
 * - ADO project: which ADO project's repositories the list shows, the
 *   configured project by default. A repository owned by another ADO project
 *   is listed through the API's `?project=`.
 *
 * Saving hands the page `{ repo, branch, project? }`; `project` is set only
 * when ADO says another project owns the repository. The page PUTs the config
 * without `enabled`, so adding a repository never turns grooming on. Every
 * entry that can be picked also saves: the config accepts every name ADO
 * allows (`isAdoName`, `isGitBranchName`); anything else is greyed with a
 * plain reason.
 */

/** A select-like control (design `.sel`). */
const SELECT_BOX =
  "flex h-8 w-full items-center gap-2 rounded-lg border border-[#3F3F46] bg-[#111113] px-2.5 text-[13px] text-foreground";

/**
 * API code for a git read ADO refused while the same token still reads work
 * items (epic PI-SPEC-SW-E288): no Code (Read) scope on the token, or no
 * permission on the account to read the repositories. ADO does not say which.
 */
export const ADO_CODE_READ_REFUSED = "ADO_CODE_READ_REFUSED";

/** The plain sentence for a list error, by API code; exported for tests. */
export function repoListErrorText(error: unknown, what: "repositories" | "branches"): { text: string; connect: boolean } {
  if (getApiErrorCode(error) === ADO_CODE_READ_REFUSED) {
    return {
      text: `Azure DevOps won't list the ${what} with your token. Make sure the token has Code: Read as well as Work Items: Read & write, and that your account can read the project's repositories.`,
      connect: true,
    };
  }
  const patCode = getAdoPatErrorCode(error);
  if (patCode === "ADO_PAT_REQUIRED") {
    return { text: `Connect Azure DevOps first so SpecTree can list the ${what}.`, connect: true };
  }
  if (patCode === "ADO_PAT_INVALID") {
    return {
      text: `Azure DevOps did not accept your token, so SpecTree cannot list the ${what}. It may have expired. Make a new one with Work Items: Read & write and Code: Read ticked.`,
      connect: true,
    };
  }
  return { text: `The ${what} could not be read from Azure DevOps. Try again in a moment.`, connect: false };
}

/** The ADO PAT problem, or any other failure, of the repository or branch list, in plain words. */
function ListError({ error, what }: { error: unknown; what: "repositories" | "branches" }) {
  const location = useLocation();
  const { text, connect } = repoListErrorText(error, what);
  return (
    <p
      className={cn(TOOLBOX_TEXT.content, "text-destructive")}
      role="alert"
      data-testid="repo-picker-error"
    >
      {text}
      {connect ? (
        <>
          {" "}
          <Link
            className="text-link underline-offset-2 hover:underline"
            to={adoSetupRedirect(location.pathname, location.search)}
          >
            Connect Azure DevOps
          </Link>
        </>
      ) : null}
    </p>
  );
}

/** Why an entry cannot be picked, or null when it can. Exported for tests. */
export function unavailableReason(repository: AdoRepository): string | null {
  if (repository.alreadyAdded) return "already added";
  if (repository.defaultBranch === null) return "empty repository";
  // Never offer what the config would refuse (ADO enforces the same rules, so this is a guard).
  if (!isAdoName(repository.name, "repository")) return "name SpecTree can't save";
  if (!isAdoName(repository.project, "project")) return "project name SpecTree can't save";
  return null;
}

export interface RepoComboboxProps {
  project: string;
  repositories: readonly AdoRepository[] | undefined;
  isLoading: boolean;
  value: AdoRepository | null;
  onChange: (repository: AdoRepository) => void;
}

/** The searchable repository list (frame 1e's open dropdown). */
export function RepoCombobox({
  project,
  repositories,
  isLoading,
  value,
  onChange,
}: RepoComboboxProps) {
  const listId = useId();
  const labelId = useId();
  const inputRef = useRef<HTMLInputElement>(null);
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [active, setActive] = useState(-1);

  const shown = useMemo(() => {
    const wanted = query.trim().toLowerCase();
    const all = repositories ?? [];
    return wanted ? all.filter((r) => r.name.toLowerCase().includes(wanted)) : [...all];
  }, [repositories, query]);
  const pickable = shown.map((r) => unavailableReason(r) === null);

  /** The next pickable entry from `active` in direction `step`, wrapping round; already-added ones are skipped. */
  const move = (step: 1 | -1) => {
    const count = shown.length;
    const start = active < 0 ? (step === 1 ? -1 : 0) : active;
    const order = Array.from(
      { length: count },
      (_, i) => (start + step * (i + 1) + count * 2) % count
    );
    const next = order.find((index) => pickable[index]);
    if (next !== undefined) setActive(next);
  };

  const pick = (repository: AdoRepository) => {
    if (unavailableReason(repository) !== null) return;
    onChange(repository);
    setQuery("");
    setOpen(false);
    setActive(-1);
  };

  const onKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    if (event.key === "ArrowDown") {
      event.preventDefault();
      if (!open) setOpen(true);
      move(1);
    } else if (event.key === "ArrowUp") {
      event.preventDefault();
      if (!open) setOpen(true);
      move(-1);
    } else if (event.key === "Enter") {
      const repository = shown[active];
      if (open && repository) {
        event.preventDefault();
        pick(repository);
      }
    } else if (event.key === "Escape") {
      setOpen(false);
      setActive(-1);
    }
  };

  const activeId = active >= 0 && shown[active] ? `${listId}-${String(active)}` : undefined;

  return (
    <div className="relative min-w-0">
      <div className={cn(SELECT_BOX, open && "border-link")}>
        <Search className={cn("h-4 w-4 shrink-0", TOOLBOX_TEXT.faint)} aria-hidden="true" />
        <input
          ref={inputRef}
          className="min-w-0 flex-1 bg-transparent outline-none placeholder:text-muted-foreground"
          role="combobox"
          aria-label="Repository"
          aria-expanded={open}
          aria-controls={listId}
          aria-autocomplete="list"
          aria-activedescendant={activeId}
          placeholder={value ? value.name : "Pick a repository"}
          value={open ? query : (value?.name ?? "")}
          onFocus={() => {
            setOpen(true);
          }}
          onClick={() => {
            setOpen(true);
          }}
          onBlur={() => {
            setOpen(false);
            setActive(-1);
          }}
          onChange={(event) => {
            setQuery(event.target.value);
            setOpen(true);
            setActive(-1);
          }}
          onKeyDown={onKeyDown}
          data-testid="repo-combobox"
        />
        <ChevronDown
          className={cn("h-4 w-4 shrink-0", TOOLBOX_TEXT.faint, open && "rotate-180")}
          aria-hidden="true"
        />
      </div>
      {open && (
        <div
          className="absolute left-0 top-[calc(100%+4px)] z-30 w-[300px] rounded-[10px] border border-[#3F3F46] bg-[#18181B] p-1.5 shadow-[0_16px_40px_rgba(0,0,0,0.55)]"
          // Keep focus in the input while a click lands on an entry.
          onMouseDown={(event) => {
            event.preventDefault();
          }}
        >
          <div id={labelId} className={cn(TOOLBOX_TEXT.eyebrow, "px-2 pb-1.5 pt-1")}>
            Repositories in {project}
          </div>
          <ul
            id={listId}
            role="listbox"
            aria-labelledby={labelId}
            className="max-h-[240px] overflow-y-auto"
          >
            {isLoading ? (
              <li
                className={cn(TOOLBOX_TEXT.content, "px-2 py-1.5 text-muted-foreground")}
                role="presentation"
              >
                Reading the repositories from Azure DevOps…
              </li>
            ) : shown.length === 0 ? (
              <li
                className={cn(TOOLBOX_TEXT.content, "px-2 py-1.5 text-muted-foreground")}
                role="presentation"
              >
                {query.trim()
                  ? `No repository in ${project} matches "${query.trim()}".`
                  : `${project} has no repositories.`}
              </li>
            ) : (
              shown.map((repository, index) => {
                const reason = unavailableReason(repository);
                return (
                  <li
                    key={repository.id}
                    id={`${listId}-${String(index)}`}
                    role="option"
                    aria-selected={value?.id === repository.id}
                    aria-disabled={reason !== null}
                    className={cn(
                      "flex cursor-pointer items-center gap-2 rounded-md px-2 py-[7px] text-[13px] text-zinc-300",
                      index === active && "bg-[#27272A] text-foreground",
                      reason === null
                        ? "hover:bg-[#27272A] hover:text-foreground"
                        : cn("cursor-default", TOOLBOX_TEXT.faint)
                    )}
                    onMouseEnter={() => {
                      if (reason === null) setActive(index);
                    }}
                    onClick={() => {
                      pick(repository);
                    }}
                    data-testid="repo-option"
                  >
                    <GitBranch className="h-4 w-4 shrink-0" aria-hidden="true" />
                    <span className="min-w-0 flex-1 truncate">{repository.name}</span>
                    <span
                      className={cn(
                        TOOLBOX_TEXT.meta,
                        "shrink-0",
                        reason === null && TOOLBOX_TEXT.faint
                      )}
                    >
                      {reason ?? `default: ${repository.defaultBranch ?? ""}`}
                    </span>
                  </li>
                );
              })
            )}
          </ul>
        </div>
      )}
    </div>
  );
}

export interface BranchPickerProps {
  repository: AdoRepository | null;
  branches: readonly AdoBranch[] | undefined;
  isLoading: boolean;
  value: string;
  onChange: (branch: string) => void;
}

/** The branch list of the picked repository; disabled until one is picked. */
export function BranchPicker({
  repository,
  branches,
  isLoading,
  value,
  onChange,
}: BranchPickerProps) {
  const disabled = !repository || isLoading || !branches || branches.length === 0;
  return (
    <div className={cn("relative", disabled && "opacity-45")}>
      <select
        aria-label="Branch"
        className={cn(
          SELECT_BOX,
          "appearance-none pr-7 font-mono text-xs disabled:cursor-not-allowed"
        )}
        disabled={disabled}
        value={value}
        onChange={(event) => {
          onChange(event.target.value);
        }}
        data-testid="branch-picker"
      >
        {!repository ? (
          <option value="">branch</option>
        ) : isLoading || !branches ? (
          <option value="">Loading…</option>
        ) : (
          branches.map((branch) => {
            const usable = isGitBranchName(branch.name);
            const label = branch.isDefault ? `${branch.name} (default)` : branch.name;
            return (
              <option key={branch.name} value={branch.name} disabled={!usable}>
                {usable ? label : `${label} (name SpecTree can't save)`}
              </option>
            );
          })
        )}
      </select>
      <ChevronDown
        className={cn("pointer-events-none absolute right-2 top-2 h-4 w-4", TOOLBOX_TEXT.faint)}
        aria-hidden="true"
      />
    </div>
  );
}

export interface AddRepoRowProps {
  project: string;
  /** The grid columns the table rows share. */
  gridClassName: string;
  pending: boolean;
  onSave: (repo: GroomingRepoInput) => void;
  onCancel: () => void;
}

/**
 * Which ADO project's repositories the list shows (the configured project by
 * default), from the ADO projects the person's token can see. While that list
 * is loading or unreadable only the configured project is offered.
 */
export function OwnerProjectSelect({
  project,
  value,
  onChange,
}: {
  project: string;
  value: string;
  onChange: (owner: string) => void;
}) {
  const projects = useWorkItemProjects();
  const names = useMemo(() => {
    const listed = (projects.data ?? []).map((p) => p.name).filter((name) => isAdoName(name, "project"));
    const others = listed
      .filter((name) => name.toLowerCase() !== project.toLowerCase())
      .sort((a, b) => a.localeCompare(b, undefined, { sensitivity: "base" }));
    return [project, ...others];
  }, [projects.data, project]);
  return (
    <label className={cn(TOOLBOX_TEXT.meta, "flex items-center gap-2 text-muted-foreground")}>
      Repositories from ADO project
      <span className="relative">
        <select
          aria-label="ADO project the repository is in"
          className={cn(SELECT_BOX, "w-auto min-w-[160px] appearance-none pr-7")}
          value={value}
          onChange={(event) => {
            onChange(event.target.value);
          }}
          data-testid="repo-owner-project"
        >
          {names.map((name) => (
            <option key={name} value={name}>
              {name.toLowerCase() === project.toLowerCase() ? `${name} (this project)` : name}
            </option>
          ))}
        </select>
        <ChevronDown
          className={cn("pointer-events-none absolute right-2 top-2 h-4 w-4", TOOLBOX_TEXT.faint)}
          aria-hidden="true"
        />
      </span>
    </label>
  );
}

/** The new row in the Admin's table: ADO project, repository and branch pickers, Save and Cancel. */
export function AddRepoRow({ project, gridClassName, pending, onSave, onCancel }: AddRepoRowProps) {
  const [owner, setOwner] = useState(project);
  const repositories = useAdoRepositories(project, owner);
  const [picked, setPicked] = useState<AdoRepository | null>(null);
  const branches = useAdoBranches(project, picked?.id ?? null, picked?.project);
  const [branch, setBranch] = useState("");

  // Default to the repository's default branch once its branches are read;
  // a branch the Admin already chose from the list is kept. Only names the
  // config accepts are chosen.
  useEffect(() => {
    const list = branches.data?.filter((b) => isGitBranchName(b.name));
    if (!picked || !list) return;
    const preferred =
      list.find((b) => b.name === picked.defaultBranch) ?? list.find((b) => b.isDefault) ?? list[0];
    setBranch((current) =>
      list.some((b) => b.name === current) ? current : (preferred?.name ?? "")
    );
  }, [picked, branches.data]);

  const save = () => {
    if (!picked || !branch || !isGitBranchName(branch)) return;
    const input: GroomingRepoInput = { repo: picked.name, branch };
    if (picked.project.toLowerCase() !== project.toLowerCase()) input.project = picked.project;
    onSave(input);
  };

  return (
    <li
      className="border-b border-[#141417] px-[18px] py-3 last:border-b-0"
      data-testid="add-repo-row"
    >
      <div className="mb-2">
        <OwnerProjectSelect
          project={project}
          value={owner}
          onChange={(next) => {
            setOwner(next);
            setPicked(null);
            setBranch("");
          }}
        />
      </div>
      <div className={gridClassName}>
        <RepoCombobox
          project={owner}
          repositories={repositories.data}
          isLoading={repositories.isLoading}
          value={picked}
          onChange={(repository) => {
            setPicked(repository);
            setBranch(repository.defaultBranch && isGitBranchName(repository.defaultBranch) ? repository.defaultBranch : "");
          }}
        />
        <BranchPicker
          repository={picked}
          branches={branches.data}
          isLoading={branches.isLoading}
          value={branch}
          onChange={setBranch}
        />
        <span className={cn(TOOLBOX_TEXT.meta, TOOLBOX_TEXT.faint)}>
          {picked ? "Copied to each computer at its next update." : "Pick a repository first"}
        </span>
        <span className="flex justify-end gap-1">
          <Button type="button" size="sm" variant="ghost" onClick={onCancel}>
            Cancel
          </Button>
          <Button
            type="button"
            size="sm"
            className="border-primary bg-primary font-semibold text-primary-foreground hover:bg-primary/90"
            disabled={!picked || !branch || !isGitBranchName(branch) || pending}
            onClick={save}
          >
            {pending ? "Saving…" : "Save"}
          </Button>
        </span>
      </div>
      {repositories.isError && (
        <div className="mt-2">
          <ListError error={repositories.error} what="repositories" />
        </div>
      )}
      {branches.isError && (
        <div className="mt-2">
          <ListError error={branches.error} what="branches" />
        </div>
      )}
    </li>
  );
}
